import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getClientById, getRepById } from '../../data/mockData';
import { Mail, Phone, MapPin, Building2, User, DollarSign, Save, CheckCircle2, Hash } from 'lucide-react';
import StatusBadge from '../../components/shared/StatusBadge';
import { isGrayscale, setGrayscale } from '../../utils/grayscale';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';

export default function ClientProfile() {
  const { user, token } = useAuth();
  const mockClient = getClientById(user.clientId);
  const [apiClient, setApiClient] = useState(null);
  const client = apiClient || mockClient;
  const rep = client ? getRepById(client.assignedRepId) : null;
  const [saved, setSaved] = useState(false);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [grayscale, setGrayscaleState] = useState(isGrayscale);
  const toggleGrayscale = () => { const next = !grayscale; setGrayscale(next); setGrayscaleState(next); };

  const [form, setForm] = useState({
    phone: client?.phone || '',
    state: client?.state || '',
    industry: client?.industry || '',
    requestedAmount: client?.requestedAmount || 0,
  });

  useEffect(() => {
    if (!user?.clientId) return;
    fetch(`${API}/clients-api/${user.clientId}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setApiClient(data); })
      .catch(() => {});
  }, [user?.clientId, token]);

  useEffect(() => {
    if (!client) return;
    setForm({
      phone: client.phone || '',
      state: client.state || '',
      industry: client.industry || '',
      requestedAmount: client.requestedAmount || 0,
    });
  }, [client?.id, client?.clientCode]);

  if (!client) return <div className="px-1 py-5 md:p-6 text-gray-400">{user?.clientId ? 'Loading profile...' : 'Profile not found.'}</div>;

  const handleSave = () => {
    Object.assign(client, form);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  // App Store guideline 5.1.1(v): users must be able to start account deletion
  // in the app. Lending records may have to be retained, so this files a
  // request with the assigned rep (or by email) rather than deleting instantly.
  const handleDeleteRequest = async () => {
    if (!window.confirm('Request deletion of your Orbit account and personal data? Our team will confirm by email. Records we are legally required to keep will be retained.')) return;
    const text = `ACCOUNT DELETION REQUEST: ${user?.full_name || user?.email || 'This client'} (${user?.email || 'no email'}, client ${client.clientCode || client.id}) asked to delete their Orbit account and personal data.`;
    const repId = client.assignedRepId;
    if (repId && token) {
      try {
        const r = await fetch(`${API}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ toId: repId, toName: rep?.name || '', text }),
        });
        if (r.ok) { setDeleteRequested(true); return; }
      } catch { /* fall through to email */ }
    }
    window.location.href = `mailto:steky26@gmail.com?subject=${encodeURIComponent('Orbit account deletion request')}&body=${encodeURIComponent(text)}`;
    setDeleteRequested(true);
  };

  const inputCls = "w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm max-md:text-base rounded-xl px-3 py-2.5 max-md:py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400";

  return (
    <div className="px-1 py-5 md:p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Profile</h1>
        <p className="text-gray-400 text-sm mt-0.5">Your business information</p>
      </div>

      {saved && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl px-4 py-3">
          <CheckCircle2 size={14} className="text-green-600" />
          <p className="text-green-700 text-sm font-medium">Profile updated</p>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-5 max-md:items-start max-md:gap-3">
          <div className="flex items-center gap-3 max-md:min-w-0">
            <div className="w-12 h-12 max-md:shrink-0 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-center text-blue-600 font-bold text-lg">
              {client.businessName[0]}
            </div>
            <div>
              <p className="text-gray-900 font-bold">{client.businessName}</p>
              <p className="text-gray-400 text-sm">{client.ownerName}</p>
            </div>
          </div>
          <StatusBadge status={client.status} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Read-only fields */}
          <div className="flex items-center gap-3 py-3 border-b border-gray-100">
            <Hash size={15} className="text-gray-400 shrink-0" />
            <div>
              <p className="text-gray-400 text-xs">Capital Infusion Client ID</p>
              <p className="text-gray-800 text-sm font-semibold">{client.clientCode || client.id}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 py-3 border-b border-gray-100">
            <Building2 size={15} className="text-gray-400 shrink-0" />
            <div>
              <p className="text-gray-400 text-xs">Business Name</p>
              <p className="text-gray-800 text-sm font-medium">{client.businessName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 py-3 border-b border-gray-100">
            <User size={15} className="text-gray-400 shrink-0" />
            <div>
              <p className="text-gray-400 text-xs">Owner Name</p>
              <p className="text-gray-800 text-sm font-medium">{client.ownerName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 py-3 border-b border-gray-100">
            <Mail size={15} className="text-gray-400 shrink-0" />
            <div className="max-md:min-w-0">
              <p className="text-gray-400 text-xs">Email</p>
              <p className="text-gray-800 text-sm font-medium max-md:[overflow-wrap:anywhere]">{client.email}</p>
            </div>
          </div>

          {/* Editable fields */}
          <div className="py-2">
            <label className="flex items-center gap-2 text-gray-400 text-xs mb-1.5">
              <Phone size={12} /> Phone
            </label>
            <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className={inputCls} placeholder="(555) 555-0000" />
          </div>
          <div className="py-2">
            <label className="flex items-center gap-2 text-gray-400 text-xs mb-1.5">
              <MapPin size={12} /> State
            </label>
            <input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} className={inputCls} placeholder="NY" maxLength={2} />
          </div>
          <div className="py-2">
            <label className="flex items-center gap-2 text-gray-400 text-xs mb-1.5">
              <Building2 size={12} /> Industry
            </label>
            <input value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))} className={inputCls} placeholder="Automotive" />
          </div>
          <div className="py-2">
            <label className="flex items-center gap-2 text-gray-400 text-xs mb-1.5">
              <DollarSign size={12} /> Requested Amount
            </label>
            <input type="number" value={form.requestedAmount} onChange={e => setForm(f => ({ ...f, requestedAmount: e.target.value }))} className={inputCls} placeholder="75000" />
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-gray-100 flex justify-end">
          <button onClick={handleSave} className="flex items-center gap-2 max-md:w-full max-md:justify-center max-md:min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white text-sm px-5 py-2.5 rounded-xl font-medium transition-colors">
            <Save size={14} /> Save Changes
          </button>
        </div>
      </div>

      {rep && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <p className="text-gray-400 text-xs font-medium uppercase tracking-wide mb-3">Assigned Representative</p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 border border-indigo-200 rounded-full flex items-center justify-center text-indigo-600 font-bold">
              {rep.name[0]}
            </div>
            <div>
              <p className="text-gray-900 font-semibold text-sm">{rep.name}</p>
              <p className="text-gray-400 text-xs">{rep.email}</p>
              <p className="text-gray-400 text-xs">{rep.phone}</p>
            </div>
          </div>
        </div>
      )}

      {/* Display preference - phones only, so the desktop profile is unchanged */}
      <div className="md:hidden bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wide mb-2">Display</p>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p id="grayscale-label" className="text-sm font-medium text-gray-800">Grayscale display</p>
            <p className="text-xs text-gray-400 mt-0.5">Show the app in black and white</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={grayscale}
            aria-labelledby="grayscale-label"
            onClick={toggleGrayscale}
            className="shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-end"
          >
            <span className={`relative inline-flex h-7 w-12 rounded-full transition-colors ${grayscale ? 'bg-blue-600' : 'bg-gray-300'}`}>
              <span className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${grayscale ? 'translate-x-5' : ''}`} />
            </span>
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wide mb-2">Delete Account</p>
        {deleteRequested ? (
          <p className="text-sm text-gray-700">Your deletion request was sent. Our team will confirm by email.</p>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-3">Request permanent deletion of your Orbit account and personal data.</p>
            <button onClick={handleDeleteRequest} className="text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 px-4 py-2 max-md:min-h-[44px] rounded-xl transition-colors">
              Delete my account
            </button>
          </>
        )}
      </div>
    </div>
  );
}
